import { Col, Row, Spin } from 'antd';
import * as Yup from 'yup';
import {
  PageHeaderWithBackButton,
  PageHeaderWithSpace,
} from '../../../../components/common';
import { SubmitButton } from '../../../../components/common/form';
import FormFields from '../../../../components/FormFields';
import { useMerchantConnectStampCardFormBuilder } from '../helpers/useStampCardFormBuilder';
import { useUserContext } from '../../../../contexts/UserContext';
import { useFetchFlipper } from '../../../../services/merchants';
import { useFetchCompanyDetails } from '../../../../services/Company/useFetchCompanyDetails';
import { useParams } from 'react-router';
import { Button, Message } from 'fave-ui';
import { FieldArrayRenderProps, FormikHelpers, useFormikContext } from 'formik';
import ArrayForm from '../../../../components/common/form/ArrayForm';
import React, { useEffect, useRef, useState } from 'react';
import { Minus, Plus } from 'phosphor-react';
import { useMerchantConnectCreateStampCard } from '../../../../services/StampCards/useMerchantConnectCreateStampCard';
import { useFetchMerchantConnectStampCard } from '../../../../services/StampCards/useFetchMerchantConnectStampCard';
import { useMerchantConnectUpdateStampCard } from '../../../../services/StampCards/useMerchantConnectUpdateStampCard';

import { toolRedirect } from '../../../../services/http';
import {
  appendNestedPayloadToFormData,
  mutateLoyaltyRewardableSettings,
} from '../helpers/helpers';
import LockPage from '../../../../components/LockPage';
import useSetBreadCrumbParams from '../../../../hooks/useSetBreadCrumbParams';

const StampCardForm = () => {
  const { mode, id, loyaltySettingsId } = useParams();
  const companyId = String(id);
  const parsedLoyaltySettingsId = String(loyaltySettingsId);
  const isEditMode = mode === 'edit';
  const [disabledSubmitButton, setDisabledSubmitButton] = useState(false);
  const [isParsingImages, setIsParsingImages] = useState(false);
  const [selectedMaxStamps, setSelectedMaxStamps] = useState(0);

  const { getPageSettings } = useUserContext();
  const partnerSettings = getPageSettings('merchant_connect');
  const {
    data: disableFavePassOnboardingFlipper,
    isFetching: isFetchingStampCardOnboardingFlipper,
  } = useFetchFlipper({ key: 'hide_stamp_cards_onboarding' });
  const canShowStampCard =
    !!partnerSettings?.show && !!!disableFavePassOnboardingFlipper?.value;
  // update has same permissions as show fave pass
  const canUpdateStampCard = canShowStampCard;
  // disable forms based on permission
  const disableFormFields = !canUpdateStampCard;
  const isDisabledSubmitButton = disableFormFields || disabledSubmitButton;

  // fetch company details
  const { data: companyDetails, isFetching: isFetchingCompanyDetails } =
    useFetchCompanyDetails({
      company_id: companyId,
    });
  const { data: stampCardData, isFetching: isFetchingStampCardData } =
    useFetchMerchantConnectStampCard(
      { loyalty_settings_id: parsedLoyaltySettingsId },
      isEditMode,
    );

  // update breadcrumbs to show company name
  useSetBreadCrumbParams(
    [{ label: String(companyDetails?.name), key: companyId }],
    !!companyDetails,
  );

  const { mutate: createStampCard, isLoading: isLoadingCreateStampCard } =
    useMerchantConnectCreateStampCard();
  const { mutate: updateStampCard, isLoading: isLoadingUpdateStampCard } =
    useMerchantConnectUpdateStampCard();

  const {
    productDetailsFormFields,
    productDetailsInitialValues,
    productDetailsFormSchema,
    loyaltyRewardableSettingsFormFields,
    loyaltyRewardableSettingsInitialValues,
    loyaltyRewardableSettingsFormSchema,
    loyaltyRewardableSettingsBaseValues,
  } = useMerchantConnectStampCardFormBuilder({
    selectedMaxStamps,
    isEditMode,
    loyaltyRewardableSettingsData: stampCardData,
  });

  const formSchema = Yup.object().shape({
    ...productDetailsFormSchema,
    ...loyaltyRewardableSettingsFormSchema,
  });

  const pageTitle = `${isEditMode ? 'Edit' : 'Create'} Stamp Card`;
  const subTitle = (
    <span>
      {isEditMode ? 'Edit' : 'Add'} Stamp Card for{' '}
      <strong>{companyDetails?.name}</strong>
    </span>
  );

  const handleSuccess = (message: string) => {
    setDisabledSubmitButton(true);
    Message.success({ content: message }).then(() =>
      toolRedirect(
        `/admin/merchant_connect/merchants/${companyId}/loyalty_settings`,
      ),
    );
  };

  const handleFormSubmit = async (
    { ...restProductDetailsValues }: typeof productDetailsInitialValues,
    { setSubmitting }: FormikHelpers<typeof productDetailsInitialValues>,
  ) => {
    setIsParsingImages(true);
    const formData = new FormData();

    // parse images inside product details
    const parsedLoyaltyRewardableSettings =
      await mutateLoyaltyRewardableSettings(restProductDetailsValues).then(
        parsedData => {
          setIsParsingImages(false);
          return parsedData;
        },
      );

    const payload = {
      mc_product: {
        company_id: companyId,
        product_type: 'LoyaltySetting',
        product_details: {
          ...restProductDetailsValues,
          loyalty_rewardable_settings: parsedLoyaltyRewardableSettings,
        },
      },
    };

    // not needed in payload
    const excludeKeys = [
      'redemption_start_date_humanize',
      'redemption_end_date_humanize',
      'image_urls',
      'image_ids',
    ];

    await appendNestedPayloadToFormData({
      obj: payload,
      formData,
      parentKey: '',
      excludeKeys,
    });

    if (isEditMode)
      updateStampCard(
        { payload: formData, loyaltySettingsId: parsedLoyaltySettingsId },
        {
          onSuccess: () => handleSuccess('Successfully updated Stamp Card'),
          onSettled: () => setSubmitting(false),
        },
      );
    else
      createStampCard(formData, {
        onSuccess: () => handleSuccess('Successfully created Stamp Card'),
        onSettled: () => setSubmitting(false),
      });
  };

  // wait until all necessary data is fetched
  const isFormLoading =
    isFetchingCompanyDetails ||
    isFetchingStampCardOnboardingFlipper ||
    isFetchingStampCardData ||
    isLoadingCreateStampCard ||
    isLoadingUpdateStampCard ||
    isParsingImages;

  const pageHeaderRefs = useRef<(HTMLDivElement | null)[]>([]);

  // scroll to latest rewards section when user add new reward
  const scrollToLatestRewardsSection = () => {
    setTimeout(() => {
      const lastPageHeader =
        pageHeaderRefs.current[pageHeaderRefs.current.length - 1];

      if (lastPageHeader)
        lastPageHeader.scrollIntoView({
          behavior: 'smooth',
        });
    }, 500);
  };

  const ArrayFormContent = ({
    push,
    remove,
  }: {
    push: FieldArrayRenderProps['push'];
    remove: FieldArrayRenderProps['remove'];
  }) => {
    const { values } = useFormikContext<
      typeof loyaltyRewardableSettingsInitialValues &
        typeof productDetailsInitialValues
    >();

    useEffect(() => {
      setSelectedMaxStamps(values.total_stamps_count);
    }, [values.total_stamps_count]);

    return (
      <>
        <Row gutter={24}>
          <FormFields
            formFields={productDetailsFormFields}
            disableFormFields={disableFormFields}
          />
        </Row>
        {values.loyalty_rewardable_settings?.length > 0 &&
          values.loyalty_rewardable_settings?.map((_, index) => (
            <React.Fragment key={index}>
              <div ref={el => (pageHeaderRefs.current[index] = el)}>
                <PageHeaderWithSpace
                  title={`Reward #${index + 1}`}
                  subTitle={'Configure reward details'}
                  additionalContent={
                    <>
                      <Row gutter={24}>
                        <Col>
                          {values.loyalty_rewardable_settings.length > 1 && (
                            <Button
                              icon={<Minus />}
                              shape={'circle'}
                              size={'large'}
                              onClick={() => remove(index)}
                            />
                          )}
                        </Col>
                        <Col>
                          <Button
                            icon={<Plus />}
                            shape={'circle'}
                            size={'large'}
                            onClick={() => {
                              push(loyaltyRewardableSettingsBaseValues);
                              scrollToLatestRewardsSection();
                            }}
                          />
                        </Col>
                      </Row>
                    </>
                  }
                />
              </div>
              <Row gutter={24} key={index}>
                <FormFields
                  name={'loyalty_rewardable_settings'}
                  index={index}
                  formFields={loyaltyRewardableSettingsFormFields}
                  disableFormFields={disableFormFields}
                />
              </Row>
            </React.Fragment>
          ))}
        <div className={'flex justify-end'}>
          <SubmitButton disableIfNotValid isDisabled={isDisabledSubmitButton}>
            {(_, isSubmitting) => (isSubmitting ? 'Saving...' : 'Submit')}
          </SubmitButton>
        </div>
      </>
    );
  };

  return (
    <>
      {canShowStampCard ? (
        <>
          <PageHeaderWithBackButton title={pageTitle} subTitle={subTitle} />
          <Spin spinning={isFormLoading}>
            <div className="flex justify-center">
              <div className="max-w-[750px]">
                <ArrayForm
                  name={'loyalty_rewardable_settings'}
                  initialValues={{
                    ...loyaltyRewardableSettingsInitialValues,
                    ...productDetailsInitialValues,
                  }}
                  validationSchema={formSchema}
                  onSubmit={handleFormSubmit}
                  validateOnMount
                  validateOnChange
                  validateOnSchemaChange
                >
                  {({ push, remove }) => (
                    <ArrayFormContent push={push} remove={remove} />
                  )}
                </ArrayForm>
              </div>
            </div>
          </Spin>
        </>
      ) : (
        <LockPage />
      )}
    </>
  );
};

export default StampCardForm;
